import UnitConverter from "components/organisms/UnitConverter/UnitConverter";
import { formatNumber } from "helpers/commonHelpers";
import PropTypes from "prop-types";
import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { ReactComponent as ArrowDown } from "assets/images/icons/ArrowDown.svg";
import { ReactComponent as ArrowUp } from "assets/images/icons/ArrowUp.svg";
import { FormattedMessage } from "react-intl";

interface Item {
  handling_unit?: string;
  container_type?: string;
  length?: number;
  width?: number;
  height?: number;
  stackable?: boolean;
  is_dangerous_good?: boolean;
  handling_required?: boolean;
  units?: number;
  m_unit?: string;
  weight?: number;
  w_unit?: any;
  additional_information?: string;
  good_customs_information: {
    hs_code?: string;
    description?: string;
    currency_code?: string;
    value?: number;
  }[];
}

interface GoodItemProps {
  requested: Item;
  actual: Item;
}

function GoodItem({ requested, actual }: GoodItemProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  let requestedWeight: number = 0;
  let actualWeight: number = 0;
  if (requested.units && requested.weight) {
    requestedWeight = requested.units * requested.weight;
  }
  if (actual.units && actual.weight) {
    actualWeight = actual.units * actual.weight;
  }

  const numberOfCustomsRows = Math.max(
    requested.good_customs_information?.length || 0,
    actual.good_customs_information?.length || 0,
  );

  const isContainerHandlingUnit = [requested.handling_unit, actual.handling_unit].includes("container");
  const isHandlingRequiredForInvoice = requested.handling_required || actual.handling_required; // Depicts if Handling required option actually needs to be shown

  const renderDetails = (item: Item): JSX.Element => {
    const {
      handling_unit,
      container_type,
      length,
      width,
      height,
      stackable,
      is_dangerous_good,
      handling_required,
      units,
      m_unit,
      weight,
      w_unit,
      additional_information,
      good_customs_information,
    } = item;

    let isDangerousGood = "-";
    let isStackable = "-";
    let isHandlingRequiredText = null;
    if (Object.keys(item).length !== 0) {
      isDangerousGood = is_dangerous_good ? "Yes" : "No";
      isStackable = stackable ? "Yes" : "No";
      if (isHandlingRequiredForInvoice !== null) isHandlingRequiredText = handling_required ? "Yes" : "No";
    }

    return (
      <Col>
        <div>{handling_unit || "-"}</div>
        {isContainerHandlingUnit && <div>{container_type || "-"}</div>}
        <div>{length && width && height && m_unit ? `${length} x ${width} x ${height} x ${m_unit}` : "-"}</div>
        <div>{isStackable}</div>
        <div>{isDangerousGood}</div>
        {isHandlingRequiredForInvoice !== null && <div>{isHandlingRequiredText}</div>}
        <div>{units || "-"}</div>
        <div>{weight && w_unit ? `${weight} ${w_unit}` : "-"}</div>
        <div>{units && weight && w_unit ? `${units * weight} ${w_unit}` : "-"}</div>
        <div>{additional_information || "-"}</div>
        {good_customs_information?.length > 0 ? (
          good_customs_information.map((item, idx) => (
            <div key={idx}>
              {item.value} {item.currency_code} {item.description} {item.hs_code}
            </div>
          ))
        ) : (
          <div>-</div>
        )}
      </Col>
    );
  };

  return (
    <>
      <Row className="mt-3 mb-3">
        <Col>
          <div>{requested.handling_unit || actual.handling_unit} </div>
        </Col>
        <Col>
          {requestedWeight !== 0 ? (
            <div
            >
              <UnitConverter
                content={
                  <span>
                    {requested.units} units / {formatNumber(requestedWeight)} {requested.w_unit}
                  </span>
                }
                unit={requested.w_unit?.toLowerCase()}
                value={requestedWeight}
              />
            </div>
          ) : (
            "-"
          )}
        </Col>
        <Col>
          {actualWeight !== 0 ? (
            <div
            >
              <UnitConverter
                content={
                  <span>
                    {actual.units} units / {formatNumber(actualWeight)} {actual.w_unit}
                  </span>
                }
                unit={actual.w_unit?.toLowerCase()}
                value={actualWeight}
              />
            </div>
          ) : (
            "-"
          )}
        </Col>
        <div
          className="position-absolute cursor-pointer"
          style={{ right: 30 }}
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          {isExpanded ? <ArrowUp /> : <ArrowDown />}
        </div>
      </Row>
      {isExpanded && (
        <Row className="py-3" style={{ backgroundColor: "#F7F9FA", borderRadius: "8px" }}>
          <Col className="font-weight-bold">
            <div>
              <FormattedMessage id="new-request.packing-list.handlingUnit" defaultMessage="Handling unit" />: :
            </div>
            {isContainerHandlingUnit && (
              <div>
                <FormattedMessage id="new-request.packing-list.containerType" defaultMessage="Container type" />:
              </div>
            )}
            <div>
              <FormattedMessage id="quotation-form.viewCommodities.size" defaultMessage="Size (L x W x H)" />:
            </div>
            <div>
              <FormattedMessage id="new-request.packing-list.stackable" defaultMessage="Stackable" />:
            </div>
            <div>
              <FormattedMessage id="new-request.packing-list.dangerousGoods" defaultMessage="Dangerous goods" />:
            </div>
            {isHandlingRequiredForInvoice && (
              <div>
                <FormattedMessage id="new-request.packing-list.handlingRequired" defaultMessage="Handling required" />:
              </div>
            )}
            <div>
              <FormattedMessage id="quotation-form.viewCommodities.units" defaultMessage="Quantity" />:
            </div>
            <div>
              <FormattedMessage id="quotation-form.viewCommodities.unitWeight" defaultMessage="Unit weight" />:
            </div>
            <div>
              <FormattedMessage id="quotation-form.viewCommodities.totalWeight" defaultMessage="Total weight" />:
            </div>
            <div>
              <FormattedMessage
                id="quotation-form.viewCommodities.additionalInformation"
                defaultMessage="Additional information"
              />
              :
            </div>
            {Array(numberOfCustomsRows).fill(
              <div>
                <FormattedMessage
                  id="new-request.quote-confirmation.packing-list.customInformation"
                  defaultMessage="Customs information"
                />
                :
              </div>,
            )}
          </Col>
          {renderDetails(requested)}
          {renderDetails(actual)}
        </Row>
      )}
    </>
  );
}

GoodItem.propTypes = {
  requested: PropTypes.instanceOf(Object).isRequired,
  actual: PropTypes.instanceOf(Object).isRequired,
};

export default GoodItem;
